/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Injectable } from '@angular/core';
import { NotificationConfig, NotificationRef } from '@gh/core-ui';
import { EMPTY$, of$ } from '@gh/rx';
import { delay$, switchMap$, takeUntil$ } from '@gh/rx/operators';
import { get, without } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { DefaultNotificationRef, Notification, NotificationStatus } from './notification';

export const hideDuration = 2000;

/**
 * This service controls list of notifications and lifecycle of each notification (creation, hide on delay).
 *
 * @internal this class is not intended for the public use
 */
@Injectable()
export class NotificationContext extends BehaviorSubject<Notification[]> {
  constructor() {
    super([]);
  }

  /**
   * Creates new notification and adds it into the list of notifications
   *
   * @param config
   * @returns {NotificationRef}
   */
  create(config: NotificationConfig): NotificationRef {
    const notification = new Notification(config);

    this.next([notification].concat(this.value));

    if (config.hideDelay) {
      // delay to start hiding effect
      const hidingDelay = Math.max(config.hideDelay - hideDuration, 0);
      // delay of hiding effect, after this delay notification is closed
      const closedDelay = Math.min(config.hideDelay, hideDuration);
      const statusMap = {
        [NotificationStatus.Free]: of$(NotificationStatus.Hiding).pipe(delay$(hidingDelay)),
        [NotificationStatus.Hiding]: of$(NotificationStatus.Closed).pipe(delay$(closedDelay)),
      };

      // hide notification only when it does not have blocked status
      // after blocked status we start delay from the beginning
      notification.status.pipe(switchMap$((status: NotificationStatus) => get(statusMap, status, EMPTY$)),
                               takeUntil$(notification.afterClosed))
        .subscribe((status: NotificationStatus) => {
          switch (status) {
            case NotificationStatus.Hiding:
              notification.hiding();
              break;
            case NotificationStatus.Closed:
              notification.close();
              break;
            default:
              break;
          }
        });
    }

    notification.afterClosed.subscribe(() => this.next(without(this.value, notification)));

    return new DefaultNotificationRef(notification);
  }

  closeAll(): void {
    this.value.forEach((notification) => notification.close());
  }
}
